import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { FaCheckCircle, FaRegEdit } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { IoIosCloseCircle } from "react-icons/io";
import { LiaEditSolid, LiaTrashAlt } from "react-icons/lia";
import AIIcon from "../../../icons/robo.gif";
import { Sparkles, RotateCcw, Loader2, AlertCircle } from "lucide-react";
import { generatePurposeArtifacts } from "../../../api/common";

import styles from "../style.module.scss";
import SubmitButton from "../../../Components/Button";

interface StateProps {
  AIQuestion: any[];
  customQuestion: any[];
  textShow: boolean;
  isValidatedCustomQuestion: string;
  question: string;
  handleCheckboxChange: (
    label: string,
    artifacts: string,
    value: boolean,
    isCustomQuestion: boolean,
    purpose?: string,
  ) => void;
  setQuestion: (value: string) => void;
  setIsValidatedCustomQuestion: (value: string) => void;
  handleAdd: (purpose?: string, artifacts?: string) => void;
  setTextShow: (value: boolean) => void;
  renderButtonClass: () => void;
  handleSubmitQuestion: () => void;
  handleUpdateQuestion: (oldLabel: any, data: string, purpose?: string, artifacts?: string) => void;
  handleDeleteQuestion: (label: string) => void;
  selectedCheckBoxQuestion: any;
  handleLeafClick: (
    ids: any,
    label: any,
    complianceId: any,
    topicName: any,
    description: any,
    themeID: any,
    policyName: any,
  ) => void;
  selectedClause: {
    clause: string;
    clauseID: string;
    topicName: string;
    recordID: number;
    description: string;
    auditThemesId: string;
    policyName: string;
  };
  questionData: {
    complianceId: number;
    clauseId: number;
    questions: { questionText: string; artifacts: string; purpose?: string }[];
    topicName: string;
    policyName: string;
  }[];
  suggestionloading: boolean;
}

const DrilldownQuestions: React.FC<StateProps> = ({
  AIQuestion,
  handleCheckboxChange,
  customQuestion,
  textShow,
  isValidatedCustomQuestion,
  question,
  setQuestion,
  setIsValidatedCustomQuestion,
  handleAdd,
  setTextShow,
  handleSubmitQuestion,
  handleUpdateQuestion,
  handleDeleteQuestion,
  selectedCheckBoxQuestion,
  handleLeafClick,
  selectedClause,
  questionData,
  suggestionloading,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [editPurpose, setEditPurpose] = useState("");
  const [editArtifacts, setEditArtifacts] = useState("");
  const [customPurpose, setCustomPurpose] = useState("");
  const [customArtifacts, setCustomArtifacts] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasError, setHasError] = useState(
    !AIQuestion || AIQuestion?.length === 0,
  );

  useEffect(() => {
    if (AIQuestion.length !== 0) {
      setHasError(false);
    } else {
      setHasError(true);
    }
  }, [AIQuestion]);

  useEffect(() => {
    if (!suggestionloading) {
      setIsRegenerating(false);
    }
  }, [suggestionloading]);
  // Get previously selected questions for the current clause
  const getPreviouslySelectedQuestions = () => {
    const previousData = questionData.find(
      (item) => item.complianceId === selectedClause?.recordID,
    );
    return previousData?.questions || [];
  };

  // Helper function to check if a question is currently selected
  const isQuestionChecked = (questionText: string): boolean => {
    return selectedCheckBoxQuestion.some(
      (q: any) => q.questionText === questionText,
    );
  };

  // Separate previous and new questions (both AI and Custom)
  const {
    previousAIQuestions,
    previousCustomQuestions,
    newAIQuestions,
    currentCustomQuestions,
  } = React.useMemo(() => {
    const newQuestions = AIQuestion || [];
    const allPreviousQuestions = getPreviouslySelectedQuestions();

    // Separate previous AI and custom questions
    const previousAI = allPreviousQuestions.filter(
      (q: any) => !q.isCustomQuestion && q.artifacts,
    );
    const previousCustom = allPreviousQuestions.filter(
      (q: any) => q.isCustomQuestion || !q.artifacts,
    );

    // Create a set of previous question texts for comparison
    const previousAIQuestionTexts = new Set(
      previousAI.map((q: any) => q.questionText.toLowerCase().trim()),
    );
    const previousCustomQuestionTexts = new Set(
      previousCustom.map((q: any) => q.questionText.toLowerCase().trim()),
    );

    // Filter new questions to exclude those already in previous
    const filteredNewQuestions = newQuestions.filter(
      (q: any) => !previousAIQuestionTexts.has(q.question.toLowerCase().trim()),
    );

    // Filter current custom questions to exclude those already in previous
    const filteredCurrentCustomQuestions = customQuestion.filter(
      (q: string) => !previousCustomQuestionTexts.has(q.toLowerCase().trim()),
    );

    return {
      previousAIQuestions: previousAI.map((q: any) => ({
        question: q.questionText,
        artifacts: q.artifacts,
        purpose: q.purpose || "",
        isCustomQuestion: false,
      })),
      previousCustomQuestions: previousCustom.map((q: any) => ({
        question: q.questionText,
        artifacts: q.artifacts || "",
        purpose: q.purpose || "",
        isCustomQuestion: true,
      })),
      newAIQuestions: filteredNewQuestions.map((q: any) => ({
        question: q.question,
        artifacts: q.artifacts,
        purpose: q.purpose,
        isCustomQuestion: false,
      })),
      currentCustomQuestions: filteredCurrentCustomQuestions,
    };
  }, [AIQuestion, questionData, selectedClause?.recordID, customQuestion]);

  const getCustomQuestionMeta = (label: string) => {
    const match = selectedCheckBoxQuestion.find(
      (q: any) => q.questionText === label,
    );
    return { purpose: match?.purpose || "", artifacts: match?.artifacts || "" };
  };

  const startEditing = (index: number, label: string) => {
    const meta = getCustomQuestionMeta(label);
    setEditingIndex(index);
    setEditText(label);
    setEditPurpose(meta.purpose);
    setEditArtifacts(meta.artifacts);
  };

  const saveEdit = (oldLabel: string) => {
    if (editText.trim()) {
      handleUpdateQuestion(oldLabel, editText.trim(), editPurpose.trim(), editArtifacts.trim());
    }
    setEditingIndex(null);
    setEditText("");
    setEditPurpose("");
    setEditArtifacts("");
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setHasError(false);
    handleLeafClick(
      selectedClause?.clauseID,
      selectedClause?.clause,
      selectedClause?.recordID,
      selectedClause?.topicName,
      selectedClause?.description,
      selectedClause?.auditThemesId,
      selectedClause?.policyName,
    );
    // Set a timeout to reset the refreshing state after API call completes
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const handleGeneratePurposeArtifacts = async () => {
    if (!question.trim()) return;
    setIsGenerating(true);
    try {
      const data = await generatePurposeArtifacts(question.trim());
      const result = data?.questions?.[0];
      if (result) {
        setCustomPurpose(result.purpose || "");
        setCustomArtifacts(result.artifacts || "");
      }
    } catch {
      // silently fail — user can still fill manually
    } finally {
      setIsGenerating(false);
    }
  };

  const resetCustomFields = () => {
    setQuestion("");
    setCustomPurpose("");
    setCustomArtifacts("");
    setIsValidatedCustomQuestion("");
    setTextShow(false);
  };

  const handleRegenerateAI = () => {
    setIsRefreshing(true);
    setIsRegenerating(true);
    handleLeafClick(
      selectedClause?.clauseID,
      selectedClause?.clause,
      selectedClause?.recordID,
      selectedClause?.topicName,
      selectedClause?.description,
      selectedClause?.auditThemesId,
      selectedClause?.policyName,
    );
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  return (
    <>
      <div className="space-y-4">
        {/* Previously Selected Questions (Both AI and Custom) */}
        {(previousAIQuestions?.length > 0 ||
          previousCustomQuestions?.length > 0) && (
            <>
              <div className="flex items-center gap-2">
                <span className={`fw-bold fs-15`}>Previously Selected</span>
              </div>
              {/* Previous AI Questions */}
              {previousAIQuestions.map((item: any, index: number) => (
                <div
                  key={`prev-ai-${index}`}
                  className="d-flex items-start gap-2 pb-3 border-bottom my-3"
                >
                  <input
                    type="checkbox"
                    id={`prev-ai-question-${index}`}
                    checked={isQuestionChecked(item.question)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        item.question,
                        item.artifacts,
                        e.target.checked,
                        false,
                        item.purpose,
                      )
                    }
                    disabled={isGenerating}
                    className={`${styles.checkBox} mt-1 h-4 w-4`}
                  />
                  <label
                    htmlFor={`prev-ai-question-${index}`}
                    className="d-inline"
                  >
                    <div className="fw-semibold">
                      {item.question}
                      <span
                        className="text-muted ms-2"
                        style={{ fontSize: "0.85em" }}
                      >
                        (AI Generated)
                      </span>
                    </div>
                    {item.purpose && (
                      <div className="text-muted">
                        <strong>Purpose:</strong> {item.purpose}
                      </div>
                    )}
                    {item.artifacts && (
                      <div className="text-muted my-2">
                        <strong>Artifacts:</strong> {item.artifacts}
                      </div>
                    )}
                  </label>
                </div>
              ))}
              {/* Previous Custom Questions */}
              {previousCustomQuestions.map((item: any, index: number) => (
                <div
                  key={`prev-custom-${index}`}
                  className="d-flex items-start gap-2 pb-3 border-bottom my-3"
                >
                  <input
                    type="checkbox"
                    id={`prev-custom-question-${index}`}
                    checked={isQuestionChecked(item.question)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        item.question,
                        item.artifacts,
                        e.target.checked,
                        true,
                        item.purpose,
                      )
                    }
                    className={`${styles.checkBox} mt-1 h-4 w-4`}
                  />
                  <label
                    htmlFor={`prev-custom-question-${index}`}
                    className="d-inline"
                  >
                    <div className="fw-semibold">
                      {item.question}
                      <span
                        className="text-muted ms-2"
                        style={{ fontSize: "0.85em" }}
                      >
                        (Custom)
                      </span>
                    </div>
                    {item.purpose && (
                      <div className="text-muted">
                        <strong>Purpose:</strong> {item.purpose}
                      </div>
                    )}
                    {item.artifacts && (
                      <div className="text-muted my-2">
                        <strong>Artifacts:</strong> {item.artifacts}
                      </div>
                    )}
                  </label>
                </div>
              ))}
            </>
          )}
        {suggestionloading ? (
          <div className={styles.loadingWrapper}>
            <div className={styles.loadingSpinner}>
              <div className={styles.spinnerCircle}></div>
            </div>
            <p className={styles.loadingText}>
              {isRegenerating ? "Regenerating questions..." : "Generating questions..."}
            </p>
          </div>
        ) : (
          newAIQuestions?.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <img
                    src={AIIcon}
                    alt="AI Icon"
                    className={styles.suggestionHeading}
                    style={{ width: "30px", height: "30px" }}
                  />
                  <span className={`fw-bold fs-15`}>
                    AI Suggested Questions
                  </span>
                </div>
                <SubmitButton
                  onClick={handleRegenerateAI}
                  className={styles.regenerateBtn}
                >
                  <RotateCcw size={14} /> Regenerate Questions
                </SubmitButton>
              </div>
              {newAIQuestions.map((item: any, index: number) => (
                <div
                  key={`new-${index}`}
                  className="d-flex items-start gap-2 pb-3 border-bottom my-3"
                >
                  <input
                    type="checkbox"
                    id={`new-question-${index}`}
                    checked={isQuestionChecked(item.question)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        item.question,
                        item.artifacts,
                        e.target.checked,
                        false,
                        item.purpose,
                      )
                    }
                    className={`${styles.checkBox} mt-1 h-4 w-4`}
                  />
                  <label htmlFor={`new-question-${index}`} className="d-inline">
                    <div className="fw-semibold">{item.question}</div>
                    <div className="text-muted">
                      <strong>Purpose:</strong> {item.purpose}
                    </div>
                    <div className="text-muted my-2">
                      <strong>Artifacts:</strong> {item.artifacts}
                    </div>
                  </label>
                </div>
              ))}
            </>
          )
        )}
      </div>
      <div>
        {hasError && !suggestionloading ? (
          <div className={styles.wrappers}>
            <div className={styles.errorCard}>
              <div className={styles.errorIconWrapper}>
                <AlertCircle className={styles.errorIcons} />
              </div>

              <div className={styles.errorText}>
                <h3>We couldn’t load AI-suggested questions</h3>
                <p>No worries, this happens sometimes. Please try again.</p>
                <p>
                  You can continue adding your own questions in the meantime.
                </p>
              </div>
              <RotateCcw className={styles.icon} onClick={handleRefresh} />
            </div>
          </div>
        ) : null}
      </div>
      {currentCustomQuestions?.length > 0 && (
        <div className="my-3">
          <div className="my-3">
            <span className="d-flex fw-bold align-items-center fs-15">
              <LiaEditSolid size={22} />
              &nbsp;Custom Questions
            </span>
          </div>

          {currentCustomQuestions.map((label, index) => {
            const meta = getCustomQuestionMeta(label);
            return (
              <div
                key={index}
                className="d-flex flex-column gap-2 pb-3 border-bottom my-3"
              >
                {editingIndex === index ? (
                  <div className="flex-grow-1 border rounded p-2">
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      placeholder="Question"
                      className="shadow-none no-resize mb-2 fs-14"
                    />
                    <div className={styles.custom_box_fields}>
                      <div>
                        <div className={styles.custom_box_field_label}>Purpose</div>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={editPurpose}
                          onChange={(e) => setEditPurpose(e.target.value)}
                          placeholder="Purpose (optional)"
                          className="shadow-none no-resize fs-14"
                        />
                      </div>
                      <div>
                        <div className={styles.custom_box_field_label}>Artifacts</div>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={editArtifacts}
                          onChange={(e) => setEditArtifacts(e.target.value)}
                          placeholder="Artifacts (optional)"
                          className="shadow-none no-resize fs-14"
                        />
                      </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <IoIosCloseCircle
                        size={20}
                        className="modal-Icons Close-Icon"
                        onClick={() => setEditingIndex(null)}
                        style={{ cursor: "pointer" }}
                      />
                      <FaCheckCircle
                        size={17}
                        className="modal-Icons Check-Icon"
                        onClick={() => saveEdit(label)}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="d-flex align-items-start gap-2">
                    <input
                      type="checkbox"
                      id={`question-${label}`}
                      checked={isQuestionChecked(label)}
                      onChange={(e) =>
                        handleCheckboxChange(label, meta.artifacts, e.target.checked, true, meta.purpose)
                      }
                      className={`${styles.checkBox} mt-1 h-4 w-4`}
                    />
                    <label
                      htmlFor={`question-${label}`}
                      className={
                        styles.customQuestionLabel +
                        " flex-1 d-flex justify-content-between align-items-start"
                      }
                    >
                      <div>
                        <span className="font-medium">{label}</span>
                        {meta.purpose && (
                          <div className="text-muted mt-1">
                            <strong>Purpose:</strong> {meta.purpose}
                          </div>
                        )}
                        {meta.artifacts && (
                          <div className="text-muted mt-1">
                            <strong>Artifacts:</strong> {meta.artifacts}
                          </div>
                        )}
                      </div>
                      <div className="d-flex gap-2 ms-2 align-items-center">
                        <FaRegEdit
                          size={18}
                          onClick={() => startEditing(index, label)}
                          className="modal-Icons"
                          style={{ cursor: "pointer" }}
                        />
                        <LiaTrashAlt
                          size={21}
                          onClick={() => handleDeleteQuestion(label)}
                          fill={"#921D1D"}
                          className="modal-Icons"
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {textShow && (
        <>
          {isValidatedCustomQuestion && (
            <div className={styles.error}>This question already exists.</div>
          )}
          <div
            className={styles.custom_box}
            style={{
              border: isValidatedCustomQuestion ? "1px solid red" : "",
              position: "relative",
            }}
          >
            <IoIosCloseCircle
              size={22}
              className="modal-Icons Close-Icon"
              onClick={() => resetCustomFields()}
              style={{
                cursor: "pointer",
                position: "absolute",
                top: "8px",
                right: "8px",
              }}
            />
            <textarea
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                setIsValidatedCustomQuestion("");
              }}
              placeholder="Type your custom question..."
              rows={3}
            />
            <div className={styles.custom_box_fields}>
              <div>
                <div className={styles.custom_box_field_label}>Purpose</div>
                <textarea
                  value={customPurpose}
                  onChange={(e) => setCustomPurpose(e.target.value)}
                  disabled={!question.trim() || isGenerating}
                  placeholder="Enter purpose or click generate..."
                  rows={4}
                />
              </div>
              <div>
                <div className={styles.custom_box_field_label}>Artifacts</div>
                <textarea
                  value={customArtifacts}
                  onChange={(e) => setCustomArtifacts(e.target.value)}
                  disabled={!question.trim() || isGenerating}
                  placeholder="Enter artifacts or click generate..."
                  rows={4}
                />
              </div>
            </div>
            <div className={styles.custom_box_actions}>
              <button
                type="button"
                className={styles.generate_btn}
                disabled={!question.trim() || isGenerating}
                onClick={handleGeneratePurposeArtifacts}
              >
                {isGenerating ? (
                  <><Loader2 size={14} className="spin-animation" /> Generating...</>
                ) : (
                  <><Sparkles size={14} /> Generate Purpose & Artifacts</>
                )}
              </button>
              {question.trim() && (
                <button onClick={() => handleAdd(customPurpose, customArtifacts)}>+ Add</button>
              )}
            </div>
          </div>
        </>
      )}
      <div className={"d-flex justify-content-end gap-3 my-3"}>
        <SubmitButton
          className={styles.custom_question_btn}
          onClick={() => { setTextShow(!textShow); setCustomPurpose(""); setCustomArtifacts(""); setQuestion(""); setIsValidatedCustomQuestion(""); }}
          disabled={textShow}
        >
          <FaPlus size={15} /> {"Add Custom Question"}
        </SubmitButton>
        <SubmitButton
          onClick={() => { handleSubmitQuestion(); setHasError(false); }}
          disabled={selectedCheckBoxQuestion.length === 0}
          className={styles.confirmSelectionbtn}
        >
          {"Select Questions"}
        </SubmitButton>
      </div >
    </>
  );
};

export default DrilldownQuestions;
